import { useState } from 'react'
import { CheckCircle2, Globe, Loader2 } from 'lucide-react'

import { cn, formatUsd } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import useMutationBuyDomain from '../../api/hooks/useMutationBuyDomain'
import { DomainSearchItem } from '@/modules/admin/type'

type ResultRowProps = {
	item: DomainSearchItem
	remainingUsd: number
	markupUsd: number
}

export default function ResultRow({
	item,
	remainingUsd,
	markupUsd,
}: ResultRowProps) {
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [purchased, setPurchased] = useState(false)
	const { mutate: buyDomain, isPending } = useMutationBuyDomain()

	const price = item.priceUsd ?? 0
	const baseCost = Math.max(price - markupUsd, 0)
	const overBudget = price > remainingUsd
	const remainingAfter = remainingUsd - price

	const handleBuy = () => {
		buyDomain(
			{ domain: item.domain },
			{
				onSuccess: () => {
					setPurchased(true)
					setConfirmOpen(false)
				},
			},
		)
	}

	if (purchased) {
		return (
			<div className="flex items-center justify-between rounded-md border border-green-500/40 bg-green-50/50 p-3 dark:bg-green-950/20">
				<div className="flex items-center gap-2">
					<CheckCircle2 className="h-4 w-4 text-green-600" />
					<span className="break-all font-medium">{item.domain}</span>
				</div>
				<Badge variant="success">ซื้อแล้ว</Badge>
			</div>
		)
	}

	if (!item.available) {
		return (
			<div className="flex items-center justify-between rounded-md border p-3 opacity-70">
				<div className="flex items-center gap-2">
					<Globe className="h-4 w-4 text-muted-foreground" />
					<span className="break-all">{item.domain}</span>
				</div>
				<Badge variant="secondary">{item.reason || 'ไม่ว่าง'}</Badge>
			</div>
		)
	}

	return (
		<>
			<div className="flex items-center justify-between gap-3 rounded-md border p-3">
				<div className="flex min-w-0 items-center gap-2">
					<Globe className="h-4 w-4 flex-shrink-0 text-primary" />
					<span className="break-all font-medium">{item.domain}</span>
				</div>
				<div className="flex flex-shrink-0 items-center gap-3">
					<div className="text-right">
						<div className="font-semibold">{formatUsd(price)}</div>
						{overBudget && (
							<div className="text-xs text-destructive">งบไม่เพียงพอ</div>
						)}
					</div>
					<Button
						size="sm"
						disabled={overBudget || isPending}
						onClick={() => setConfirmOpen(true)}
					>
						ซื้อ
					</Button>
				</div>
			</div>

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการซื้อโดเมน</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-3 text-sm">
								<p>
									คุณต้องการซื้อโดเมน{' '}
									<span className="break-all font-semibold text-foreground">
										{item.domain}
									</span>{' '}
									ใช่หรือไม่
								</p>
								<div className="space-y-1 rounded-md bg-muted p-3 text-foreground">
									<div className="flex justify-between">
										<span className="text-muted-foreground">ราคาโดเมน</span>
										<span>{formatUsd(baseCost)}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">
											ค่าบริการแพลตฟอร์ม
										</span>
										<span>+{formatUsd(markupUsd)}</span>
									</div>
									<div className="mt-1 flex justify-between border-t pt-1 font-semibold">
										<span>รวมทั้งสิ้น</span>
										<span>{formatUsd(price)}</span>
									</div>
								</div>
								<div className="flex justify-between text-xs text-muted-foreground">
									<span>งบประมาณคงเหลือหลังซื้อ</span>
									<span
										className={cn(remainingAfter < 0 && 'text-destructive')}
									>
										{formatUsd(remainingAfter)}
									</span>
								</div>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isPending}>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault()
								handleBuy()
							}}
							disabled={isPending}
						>
							{isPending ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									กำลังซื้อ...
								</>
							) : (
								'ยืนยันซื้อ'
							)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
